// frontend-participant/src/plain_form.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import "./app.css";

/* ──────────────────────────────────────────────────────────────
   Scenario side panel (audio player only) - MATCHES app.js
────────────────────────────────────────────────────────────── */
const ScenarioPane = ({ text }) => {
  const [width, setWidth] = useState(380);
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = (e) => {
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    e.preventDefault();
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const delta = e.clientX - startXRef.current;
      const newWidth = Math.min(Math.max(startWidthRef.current + delta, 280), 600);
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  return (
    <aside className="scenario-pane" style={{ width: `${width}px` }}>
      <div className="scenario-header">🚗� Scenario</div>
      <div className="scenario-subheading">
        This scenario contains everything you know about the accident. Use it to look up relevant information when filling out the form.
      </div>
      
      {/* Audio Player Section */}
      <div className="scenario-audio-section">
        <div className="audio-label">🎧 Listen to the scenario</div>
        <audio 
          controls 
          className="scenario-audio-player"
          preload="metadata"
          controlsList="nodownload"
        >
          <source 
            src={process.env.NODE_ENV === 'development' 
              ? "http://localhost:5000/crash_audio.mp3" 
              : "/crash_audio.mp3"
            } 
            type="audio/mpeg" 
          />
          Your browser does not support the audio element.
        </audio>
      </div>

      {/* Spacer to push task prompt to bottom */}
      <div style={{ flex: 1 }} />

      {/* Prominent Task Instruction - Stuck to bottom */}
      <div className="scenario-task-prompt">
        <div className="task-prompt-icon">⚡</div>
        <div className="task-prompt-content">
          <div className="task-prompt-title">Your Goal</div>
          <div className="task-prompt-text">
            Complete the accident report as <strong>quickly and accurately</strong> as possible. 
            Fill out all required fields based on the information you know from the scenario.
          </div>
        </div>
      </div>

      <div 
        className="resize-handle" 
        onMouseDown={handleMouseDown}
        style={{ cursor: isResizing ? 'ew-resize' : 'ew-resize' }}
      />
    </aside>
  );
};

/* ──────────────────────────────────────────────────────────────
   Formatting helpers (unchanged)
────────────────────────────────────────────────────────────── */
const fmtYesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "—");
const fmtTime = (v) =>
  typeof v === "string" && /^\d{2}:\d{2}(:\d{2})?$/.test(v) ? v.slice(0, 5) : v ?? "—";
const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const LABELS = {
  description_of_accident: "Accident Description",
  date_of_accident: "Date",
  time_of_accident: "Time",
  location_of_accident: "Location",
  road_surface_condition: "Road Surface",
  weather: "Weather",
  lighting: "Lighting",
  number_of_vehicles_involved: "Vehicles Involved",
  apparent_contributing_factors: "Contributing Factors",
  any_injuries: "Any Injuries?",
  non_vehicle_property_damage: "Property Damage (Non-Vehicle)?",
  witnesses_present: "Witnesses Present?",
  additional_comments: "Additional Comments",
};

/* ──────────────────────────────────────────────────────────────
   NEW: Validation helpers
   – required unless question text ends with “(optional)”
   – type checks: date, time, number, boolean, single/multi choice
   – rule-based requiredness for follow-ups and vehicles
────────────────────────────────────────────────────────────── */
const isOptional = (q) =>
  typeof q?.question === "string" && /\(optional\)\s*$/i.test(q.question.trim());

const isEmpty = (v) =>
  v === undefined ||
  v === null ||
  v === "" ||
  (Array.isArray(v) && v.length === 0);

const validDate = (s) => {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(s + "T00:00:00");
  return !Number.isNaN(d.getTime()) && s === d.toISOString().slice(0, 10);
};

const validTime = (s) => {
  if (typeof s !== "string") return false;
  const m = s.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
  if (!m) return false;
  const hh = +m[1], mm = +m[2];
  return hh >= 0 && hh < 24 && mm >= 0 && mm < 60;
};

const oneOf = (v, options) => options.includes(v);

const otherChosenButMissingText = (v) => {
  // We normalize “Other” to strings like "Other: some text"
  if (typeof v === "string") return /^other\s*:?$/i.test(v.trim());
  if (Array.isArray(v)) return v.some((x) => /^other\s*:?$/i.test(String(x).trim()));
  return false;
};

const parseVehicleCount = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    // "1" | "2" | "3" | "Other: 4"
    const m = value.match(/(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  }
  return null;
};

const pushError = (errors, path, msg) => {
  if (!errors[path]) errors[path] = msg;
};

function validateFieldByType(q, value, path, errors) {
  switch (q.type) {
    case "text":
    case "multiline_text": {
      if (!isOptional(q) && isEmpty(value)) pushError(errors, path, "This field is required.");
      return;
    }
    case "date": {
      if (isEmpty(value)) {
        if (!isOptional(q)) pushError(errors, path, "Please select a date.");
      } else if (!validDate(value)) {
        pushError(errors, path, "Use YYYY-MM-DD (e.g., 2025-06-12).");
      }
      return;
    }
    case "time": {
      if (isEmpty(value)) {
        if (!isOptional(q)) pushError(errors, path, "Please enter a time.");
      } else if (!validTime(value)) {
        pushError(errors, path, "Use HH:MM (e.g., 14:35).");
      }
      return;
    }
    case "number": {
      if (isEmpty(value)) {
        if (!isOptional(q)) pushError(errors, path, "Please enter a number.");
      } else if (typeof value !== "number" || Number.isNaN(value)) {
        pushError(errors, path, "Please enter a valid number.");
      }
      return;
    }
    case "boolean": {
      if (value !== true && value !== false) {
        pushError(errors, path, "Please choose Yes or No.");
      }
      return;
    }
    case "single_choice": {
      if (isEmpty(value)) {
        pushError(errors, path, "Please pick an option.");
      } else {
        const opts = q.options || [];
        const ok = typeof value === "string" && (oneOf(value, opts) || /^other:/i.test(value));
        if (!ok) pushError(errors, path, "Please choose a valid option.");
        if (q.other_specify && otherChosenButMissingText(value)) {
          pushError(errors, path, "Please specify what you mean by “Other”.");
        }
      }
      return;
    }
    case "multiple_choice": {
      const arr = Array.isArray(value) ? value : [];
      if (!arr.length && !isOptional(q)) {
        pushError(errors, path, "Select at least one option.");
      } else {
        const opts = q.options || [];
        const allOk = arr.every(
          (v) => oneOf(v, opts) || (typeof v === "string" && /^other:/.test(v))
        );
        if (!allOk) pushError(errors, path, "Some choices are invalid.");
        if (q.other_specify && otherChosenButMissingText(arr)) {
          pushError(errors, path, "Please specify what you mean by “Other”.");
        }
      }
      return;
    }
    default:
      return;
  }
}

function validateAll(schema, data) {
  const errors = {};

  // map for quick lookup by id
  const byId = {};
  for (const q of schema.questions) byId[q.id] = q;

  // Top-level questions
  for (const q of schema.questions) {
    const v = data[q.id];

    // Follow-up gating is handled by rendering, but we still guard here:
    if (q.id === "any_injuries") {
      validateFieldByType(q, v, "any_injuries", errors);
      if (v === true && q.followup_if_yes) {
        // injury table required if injuries true
        const t = q.followup_if_yes;
        const rows = Array.isArray(data[t.id]) ? data[t.id] : [];
        if (rows.length === 0) {
          pushError(errors, t.id, "Please add at least one injured person.");
        } else {
          rows.forEach((row, i) => {
            for (const col of t.columns || []) {
              const cell = row?.[col.id];
              const path = `${t.id}[${i}].${col.id}`;
              if (col.type === "single_choice") {
                validateFieldByType(col, cell, path, errors);
              } else {
                if (isEmpty(cell)) pushError(errors, path, "This field is required.");
              }
            }
          });
        }
      }
      continue;
    }

    if (q.id === "non_vehicle_property_damage") {
      validateFieldByType(q, v, "non_vehicle_property_damage", errors);
      if (v === true && q.followup_if_yes) {
        const f = q.followup_if_yes;
        const fv = data[f.id];
        if (isEmpty(fv)) pushError(errors, f.id, "Please describe the property damage.");
      }
      continue;
    }

    if (q.id === "witnesses_present") {
      validateFieldByType(q, v, "witnesses_present", errors);
      if (v === true && q.followup_if_yes) {
        const f = q.followup_if_yes;
        const fv = data[f.id];
        if (isEmpty(fv)) pushError(errors, f.id, "Please provide witness names/contacts.");
      }
      continue;
    }

    if (q.type === "group") {
      const gv = (v && typeof v === "object") ? v : {};
      for (const f of q.fields || []) {
        const pv = gv[f.id];
        validateFieldByType(f, pv, `${q.id}.${f.id}`, errors);
      }
      continue;
    }

    if (q.type === "repeat_group" && q.id === "vehicles") {
      const items = Array.isArray(v) ? v : [];
      // Optional rule: require N vehicles according to number_of_vehicles_involved, if provided.
      const numQ = byId["number_of_vehicles_involved"];
      const count = parseVehicleCount(data["number_of_vehicles_involved"]);
      if (count !== null && count > 0 && items.length < count) {
        pushError(
          errors,
          "vehicles",
          `Please add details for ${count} vehicle${count > 1 ? "s" : ""}.`
        );
      }
      // Validate each vehicle row’s fields
      items.forEach((it, idx) => {
        for (const f of q.fields || []) {
          const pv = it?.[f.id];
          validateFieldByType(f, pv, `vehicles[${idx}].${f.id}`, errors);
        }
      });
      continue;
    }

    // Regular scalar questions (including choices & multiple_choice)
    validateFieldByType(q, v, q.id, errors);
  }

  // By default, mark “additional_comments” as optional (matches schema label)
  if (errors["additional_comments"]) delete errors["additional_comments"];

  const firstPath = Object.keys(errors)[0] || null;
  return { errors, firstPath };
}

/* ──────────────────────────────────────────────────────────────
   UI bits: small wrappers to show inline errors nicely
────────────────────────────────────────────────────────────── */
function FieldWrap({ path, error, showError, children }) {
  const invalid = showError && !!error;
  return (
    <div className={`field-wrap ${invalid ? "invalid" : ""}`} data-path={path}>
      {children}
      {invalid && <div className="field-error">{error}</div>}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   ChoiceButtons — unchanged visual, now accepts path/errors
────────────────────────────────────────────────────────────── */
function ChoiceButtons({ options, multiple=false, allowOther=false, value, onChange, path, error, showError }) {
  const [local, setLocal] = useState(() => {
    if (multiple) return Array.isArray(value) ? value : [];
    return value ?? null;
  });
  const [otherText, setOtherText] = useState("");

  useEffect(() => {
    // lift state
    if (multiple) {
      const payload = allowOther && local.includes("Other") ? { value: local, other: otherText } : { value: local };
      onChange(payload);
    } else {
      const payload = allowOther && local === "Other" ? { value: local, other: otherText } : { value: local };
      onChange(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local, otherText]);

  useEffect(() => {
    // sync if parent changes value
    if (multiple) {
      if (Array.isArray(value)) setLocal(value);
    } else {
      setLocal(value ?? null);
    }
  }, [value, multiple]);

  const toggle = (opt) => {
    if (!multiple) {
      setLocal(opt);
      return;
    }
    setLocal((prev) => (prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]));
  };

  const selected = (opt) => (multiple ? local.includes(opt) : local === opt);

  return (
    <FieldWrap path={path} error={error} showError={showError}>
      <div className="ui-component">
        <div className="ui-options">
          {options.map((opt) => (
            <button
              type="button"
              key={opt}
              className={`ui-option-button ${selected(opt) ? "selected" : ""} ${
                opt === "Other" && selected(opt) && allowOther && !otherText.trim() ? "needs-specification" : ""
              }`}
              onClick={() => toggle(opt)}
            >
              {multiple && <span className="checkbox">{selected(opt) ? "☑" : "☐"}</span>}
              {opt}
              {opt === "Other" && selected(opt) && allowOther && !otherText.trim() && (
                <span style={{ marginLeft: 8, fontSize: "0.8rem" }}>(specify below)</span>
              )}
            </button>
          ))}
        </div>

        {allowOther &&
          ((multiple && local.includes("Other")) || (!multiple && local === "Other")) && (
            <div className="other-input-container">
              <input
                className="other-input"
                placeholder="Please specify…"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
              />
            </div>
          )}
      </div>
    </FieldWrap>
  );
}

/* ──────────────────────────────────────────────────────────────
   Field / Group / Repeat / Table — now accept path + errors
────────────────────────────────────────────────────────────── */
function Field({ q, value, onChange, path, error, showError }) {
  // Single place to normalize ChoiceButtons “Other”
  const normalizeChoiceChange = ({ value: v, other }) => {
    if (q.type === "single_choice") {
      if (v === "Other" && q.other_specify) onChange(other ? `Other: ${other}` : "Other");
      else onChange(v);
      return;
    }
    if (q.type === "multiple_choice") {
      const arr = Array.isArray(v) ? v : [];
      const final =
        q.other_specify && arr.includes("Other")
          ? other
            ? [...arr.filter((x) => x !== "Other"), `Other: ${other}`]
            : arr
          : arr;
      onChange(final);
      return;
    }
  };

  if (q.type === "single_choice" || q.type === "multiple_choice") {
    const multiple = q.type === "multiple_choice";
    return (
      <ChoiceButtons
        options={q.options || []}
        multiple={multiple}
        allowOther={!!q.other_specify}
        value={value}
        onChange={normalizeChoiceChange}
        path={path}
        error={error}
        showError={showError}
      />
    );
  }

  // Basic inputs inside a wrapper to show errors
  const inputProps = {};
  const content =
    q.type === "multiline_text" ? (
      <textarea
        className="other-input"
        rows={4}
        placeholder={q.question}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        {...inputProps}
      />
    ) : q.type === "text" ? (
      <input
        className="other-input"
        placeholder={q.question}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        {...inputProps}
      />
    ) : q.type === "date" ? (
      <input
        className="other-input"
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        {...inputProps}
      />
    ) : q.type === "time" ? (
      <input
        className="other-input"
        type="time"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        {...inputProps}
      />
    ) : q.type === "number" ? (
      <input
        className="other-input"
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        {...inputProps}
      />
    ) : q.type === "boolean" ? (
      <div className="ui-component">
        <div className="ui-options">
          <button
            type="button"
            className={`ui-option-button ${value === true ? "selected" : ""}`}
            onClick={() => onChange(true)}
          >
            Yes
          </button>
          <button
            type="button"
            className={`ui-option-button ${value === false ? "selected" : ""}`}
            onClick={() => onChange(false)}
          >
            No
          </button>
        </div>
      </div>
    ) : (
      <div className="muted">
        Unsupported field type: {q.type}
        <pre style={{ fontSize: '0.7rem', marginTop: 4 }}>
          {JSON.stringify(q, null, 2)}
        </pre>
      </div>
    );

  return (
    <FieldWrap path={path} error={error} showError={showError}>
      {content}
    </FieldWrap>
  );
}

function Group({ gq, value, onChange, errors = {}, triedSubmit = false }) {
  const state = value && typeof value === "object" ? value : {};
  const set = (k, v) => onChange({ ...state, [k]: v });
  const groupLabel = gq.label || gq.question || "";
  
  return (
    <div className="ui-component">
      {groupLabel && <div className="ui-instructions">{groupLabel}</div>}
      {gq.fields.map((f) => (
        <div key={f.id} id={`field-${gq.id}.${f.id}`} style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 6, fontWeight: 500 }}>
            {f.question} <span style={{ color: '#e53e3e' }}>*</span>
          </div>
          <>
            <Field
              q={f}
              value={state[f.id]}
              onChange={(v)=>set(f.id, v)}
              invalid={triedSubmit && !!errors[`${gq.id}.${f.id}`]}
            />
            {triedSubmit && errors[`${gq.id}.${f.id}`] && (
              <div className="error-message">{errors[`${gq.id}.${f.id}`]}</div>
            )}
          </>
        </div>
      ))}
    </div>
  );
}

function VehiclesRepeat({ rq, value, onChange, errors, showAll, vehicleCount }) {
  const items = Array.isArray(value) ? value : [];
  
  // Determine the number of vehicles to display
  const targetCount = vehicleCount !== null && vehicleCount > 0 ? vehicleCount : Math.max(items.length, 1);
  
  // Ensure we have the right number of items
  const displayItems = [...items];
  while (displayItems.length < targetCount) {
    displayItems.push({});
  }
  // Trim if we have too many
  const finalItems = displayItems.slice(0, targetCount);
  
  const setItem = (i, k, v) => {
    // Use items from value prop, not finalItems
    const current = Array.isArray(value) ? value : [];
    const next = [...current];
    // Ensure the array is long enough
    while (next.length <= i) {
      next.push({});
    }
    next[i] = { ...(next[i] || {}), [k]: v };
    onChange(next);
  };
  
  const add = () => {
    const current = Array.isArray(value) ? value : [];
    onChange([...current, {}]);
  };
  
  const remove = (i) => {
    const current = Array.isArray(value) ? value : [];
    const next = current.filter((_, idx) => idx !== i);
    onChange(next);
  };
  
  const repeatLabel = rq.label || rq.question || "Vehicle details";
  const fields = rq.fields || [];

  // Debug logging
  console.log('VehiclesRepeat render:', {
    vehicleCount,
    itemsLength: items.length,
    targetCount,
    finalItemsLength: finalItems.length,
    fieldsLength: fields.length,
    hasFields: fields.length > 0,
    rqId: rq.id,
    fields: fields.map(f => ({ id: f.id, type: f.type, question: f.question }))
  });

  return (
    <div className="ui-component" data-path="vehicles">
      <div className="ui-instructions">{repeatLabel}</div>
      {errors["vehicles"] && (showAll || true) && (
        <div className="field-error" style={{ marginBottom: 10 }}>{errors["vehicles"]}</div>
      )}
      {finalItems.map((it, idx) => (
        <div key={idx} className="vehicle-card" style={{ marginBottom: 12 }}>
          <div className="vehicle-title">Vehicle {idx + 1}</div>
          {fields.length === 0 ? (
            <div className="muted" style={{ padding: '10px', color: '#dc3545' }}>
              ⚠️ No vehicle fields configured in schema
              <pre style={{ fontSize: '0.7rem', marginTop: 4 }}>
                {JSON.stringify(rq, null, 2)}
              </pre>
            </div>
          ) : (
            fields.map((f) => {
              const p = `vehicles[${idx}].${f.id}`;
              const fieldLabel = f.question || f.question_text || f.id;
              console.log(`Rendering field ${f.id}:`, { fType: f.type, fQuestion: f.question, fOptions: f.options });
              return (
                <div key={f.id} style={{ marginBottom: 10 }}>
                  <div className="summary-label" style={{ marginBottom: 4 }}>
                    {fieldLabel} <span style={{ color: '#e53e3e' }}>*</span>
                  </div>
                  <Field
                    q={f}
                    value={it[f.id]}
                    onChange={(v) => setItem(idx, f.id, v)}
                    path={p}
                    error={errors[p]}
                    showError={showAll || !!errors[p]}
                  />
                </div>
              );
            })
          )}
          {finalItems.length > 1 && vehicleCount === null && (
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button type="button" className="ui-submit-button" onClick={() => remove(idx)}>
                Remove
              </button>
            </div>
          )}
        </div>
      ))}
      {vehicleCount === null && (
        <div className="ui-submit-container">
          <button type="button" className="ui-submit-button" onClick={add}>
            + Add another vehicle
          </button>
        </div>
      )}
    </div>
  );
}

function InjuryTable({ tq, value, onChange, errors, showAll }) {
  const rows = Array.isArray(value) ? value : [];
  const setCell = (i, k, v) => {
    const next = rows.slice();
    next[i] = { ...(next[i] || {}), [k]: v };
    onChange(next);
  };
  const add = () => onChange([...rows, {}]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="ui-component" data-path={tq.id}>
      <div className="ui-instructions">{tq.question}</div>
      {errors[tq.id] && (showAll || true) && (
        <div className="field-error" style={{ marginBottom: 10 }}>{errors[tq.id]}</div>
      )}
      {rows.map((row, i) => (
        <div key={i} className="vehicle-card" style={{ marginBottom: 10 }}>
          {tq.columns.map((col) => {
            const p = `${tq.id}[${i}].${col.id}`;
            return (
              <div key={col.id} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: "0.86rem", marginBottom: 4 }}>
                  {col.question} <span style={{ color: '#e53e3e' }}>*</span>
                </div>
                {col.type === "single_choice" ? (
                  <ChoiceButtons
                    options={col.options || []}
                    value={row[col.id]}
                    onChange={({ value }) => setCell(i, col.id, value)}
                    path={p}
                    error={errors[p]}
                    showError={showAll || !!errors[p]}
                  />
                ) : (
                  <Field
                    q={col}
                    value={row[col.id]}
                    onChange={(v) => setCell(i, col.id, v)}
                    path={p}
                    error={errors[p]}
                    showError={showAll || !!errors[p]}
                  />
                )}
              </div>
            );
          })}
          <button type="button" className="ui-submit-button" onClick={() => remove(i)}>
            Remove
          </button>
        </div>
      ))}
      <div className="ui-submit-container">
        <button type="button" className="ui-submit-button" onClick={add}>
          + Add injured person
        </button>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Completion summary (unchanged)
────────────────────────────────────────────────────────────── */
function CompletionSummary({ data }) {
  const s = data || {};
  const vehicles = Array.isArray(s.vehicles) ? s.vehicles : [];

  const rtc =
    s.road_type_and_controls && typeof s.road_type_and_controls === "object"
      ? s.road_type_and_controls
      : {};

  return (
    <div className="summary-card">
      <div className="summary-header">
        <div className="summary-icon">🎉</div>
        <div>
          <div className="summary-title">Form Completed Successfully!</div>
          <div className="summary-sub">Please review everything below.</div>
        </div>
      </div>

      <div className="summary-section">
        <div className="summary-section-title">Incident</div>
        <div className="summary-grid two">
          <div className="summary-row">
            <div className="summary-label">{LABELS.date_of_accident}</div>
            <div className="summary-value">{fmt(s.date_of_accident)}</div>
          </div>
          <div className="summary-row">
            <div className="summary-label">{LABELS.time_of_accident}</div>
            <div className="summary-value">{fmtTime(s.time_of_accident)}</div>
          </div>
          <div className="summary-row two-col">
            <div className="summary-label">{LABELS.location_of_accident}</div>
            <div className="summary-value">{fmt(s.location_of_accident)}</div>
          </div>
          <div className="summary-row two-col">
            <div className="summary-label">{LABELS.description_of_accident}</div>
            <div className="summary-value">{fmt(s.description_of_accident)}</div>
          </div>
        </div>
      </div>

      <div className="summary-section">
        <div className="summary-section-title">Scene Conditions</div>
        <div className="summary-grid two">
          <div className="summary-row">
            <div className="summary-label">{LABELS.weather}</div>
            <div className="summary-value">{fmt(s.weather)}</div>
          </div>
          <div className="summary-row">
            <div className="summary-label">{LABELS.road_surface_condition}</div>
            <div className="summary-value">{fmt(s.road_surface_condition)}</div>
          </div>
          <div className="summary-row">
            <div className="summary-label">{LABELS.lighting}</div>
            <div className="summary-value">{fmt(s.lighting)}</div>
          </div>
          <div className="summary-row">
            <div className="summary-label">Road Type</div>
            <div className="summary-value">{fmt(rtc.location_type)}</div>
          </div>
          <div className="summary-row">
            <div className="summary-label">Speed Limit</div>
            <div className="summary-value">
              {rtc.posted_speed_limit ? `${rtc.posted_speed_limit} mp/h` : "—"}
            </div>
          </div>
          <div className="summary-row">
            <div className="summary-label">Traffic Control</div>
            <div className="summary-value">{fmt(rtc.traffic_control)}</div>
          </div>
        </div>
      </div>

      <div className="summary-section">
        <div className="summary-section-title">
          Vehicles <span className="badge">{fmt(s.number_of_vehicles_involved)}</span>
        </div>
        {vehicles.length === 0 ? (
          <div className="muted">No vehicle details recorded.</div>
        ) : (
          <div className="vehicle-list">
            {vehicles.map((v, i) => (
              <div className="vehicle-card" key={i}>
                <div className="vehicle-title">Vehicle {i + 1}</div>
                <div className="summary-grid two">
                  <div className="summary-row two-col">
                    <div className="summary-label">Type / Make / Model</div>
                    <div className="summary-value">{fmt(v.vehicle_type_make_model)}</div>
                  </div>
                  <div className="summary-row">
                    <div className="summary-label">Licence Plate</div>
                    <div className="summary-value">{fmt(v.licence_plate)}</div>
                  </div>
                  <div className="summary-row">
                    <div className="summary-label">Pre-Crash Manoeuvre</div>
                    <div className="summary-value">{fmt(v.pre_crash_manoeuvre)}</div>
                  </div>
                  <div className="summary-row">
                    <div className="summary-label">Approx. Speed</div>
                    <div className="summary-value">
                      {v.approx_speed_mph !== undefined && v.approx_speed_mph !== null
                        ? `${v.approx_speed_mph} mp/h`
                        : v.approx_speed_kmh !== undefined && v.approx_speed_kmh !== null
                        ? `${v.approx_speed_kmh} km/h`
                        : "—"}
                    </div>
                  </div>
                  <div className="summary-row two-col">
                    <div className="summary-label">Damage</div>
                    <div className="summary-value">{fmt(v.damage_description)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="summary-section">
        <div className="summary-section-title">Outcomes</div>
        <div className="summary-grid two">
          <div className="summary-row">
            <div className="summary-label">{LABELS.any_injuries}</div>
            <div className="summary-value">{fmtYesNo(s.any_injuries)}</div>
          </div>
          <div className="summary-row">
            <div className="summary-label">{LABELS.non_vehicle_property_damage}</div>
            <div className="summary-value">{fmtYesNo(s.non_vehicle_property_damage)}</div>
          </div>
          <div className="summary-row">
            <div className="summary-label">{LABELS.witnesses_present}</div>
            <div className="summary-value">{fmtYesNo(s.witnesses_present)}</div>
          </div>
        </div>

        {s.property_damage_description && (
          <div className="summary-row two-col">
            <div className="summary-label">Property Damage Details</div>
            <div className="summary-value">{fmt(s.property_damage_description)}</div>
          </div>
        )}
        {s.witnesses_names_contacts && (
          <div className="summary-row two-col">
            <div className="summary-label">Witnesses</div>
            <div className="summary-value">{fmt(s.witnesses_names_contacts)}</div>
          </div>
        )}
      </div>

      {s.apparent_contributing_factors && (
        <div className="summary-section">
          <div className="summary-section-title">Contributing Factors</div>
          <div className="summary-row two-col">
            <div className="summary-value">
              {Array.isArray(s.apparent_contributing_factors)
                ? s.apparent_contributing_factors.join(", ")
                : fmt(s.apparent_contributing_factors)}
            </div>
          </div>
        </div>
      )}

      {s.additional_comments && (
        <div className="summary-section">
          <div className="summary-section-title">Additional Comments</div>
          <div className="summary-row two-col">
            <div className="summary-value">{fmt(s.additional_comments)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Page component — now with validation & guarded submit
────────────────────────────────────────────────────────────── */
export default function FormApp() {
  const roomId = useMemo(() => window.location.pathname.split("/").pop(), []);
  const [schema, setSchema] = useState(null);
  const [data, setData] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [triedSubmit, setTriedSubmit] = useState(false);
  const firstErrorRef = useRef(null);

  /* NEW: scenario text (window → ?scenario= → localStorage) */
  const [scenarioText] = useState(() => {
    if (typeof window === "undefined") return "";
    if (window.__SCENARIO__) return String(window.__SCENARIO__);
    const qs = new URLSearchParams(window.location.search);
    const fromQuery = qs.get("scenario");
    if (fromQuery) return fromQuery;
    const fromStorage = localStorage.getItem("scenarioText");
    return fromStorage || "";
  });

  useEffect(() => {
    (async () => {
      const baseUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:5000' 
        : window.location.origin;
      const res = await fetch(`${baseUrl}/api/form/schema`);
      const raw = await res.json();
      
      // Normalize schema to handle new structure
      const normalizeQuestion = (q) => {
        const normalized = { ...q };
        
        // Map question_text to question for consistency
        if (q.question_text) normalized.question = q.question_text;
        
        // Map bool to boolean for consistency
        if (q.type === 'bool') normalized.type = 'boolean';
        
        // Map structure_type to type for repeat_group and group
        if (q.structure_type === 'repeat_group') {
          normalized.type = 'repeat_group';
        }
        if (q.structure_type === 'group') {
          normalized.type = 'group';
        }
        
        // Handle gate structures - convert to followup_if_yes format
        if (q.structure_type === 'gate' && q.gate && q.fields) {
          // This is a gated section - we'll handle it differently
          normalized.isGate = true;
          normalized.gateOn = q.gate.gate_on;
          normalized.whenValues = q.gate.when_values;
        }
        
        // Normalize nested structures
        if (q.fields) {
          normalized.fields = q.fields.map(normalizeQuestion);
        }
        if (q.columns) {
          normalized.columns = q.columns.map(normalizeQuestion);
        }
        
        return normalized;
      };
      
      const normalized = {
        title: raw.title,
        questions: (raw.questions || []).map(normalizeQuestion)
      };
      
      setSchema(normalized);
    })();
  }, []);

  const setField = (id, val) => setData((d) => ({ ...d, [id]: val }));

  // ——— validation helpers ———
  const isBlank = (v) =>
    v === undefined || v === null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);

  const buildErrors = (form) => {
    const e = {};
    
    // All text/multiline_text fields (required)
    ["description_of_accident", "location_of_accident"]
      .forEach((id) => { if (isBlank(form[id])) e[id] = "This field is required."; });

    // Date field (required with validation)
    if (isBlank(form.date_of_accident)) {
      e.date_of_accident = "This field is required.";
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date_of_accident)) {
      e.date_of_accident = "Please enter a valid date (YYYY-MM-DD).";
    }

    // Time field (required with validation)
    if (isBlank(form.time_of_accident)) {
      e.time_of_accident = "This field is required.";
    } else if (!/^\d{2}:\d{2}(:\d{2})?$/.test(form.time_of_accident)) {
      e.time_of_accident = "Please enter a valid time (HH:MM).";
    }

    // Single choice fields (required)
    ["road_surface_condition", "weather", "lighting", "number_of_vehicles_involved"]
      .forEach((id) => { if (isBlank(form[id])) e[id] = "Please select an option."; });

    // Multiple choice field (required)
    if (isBlank(form.apparent_contributing_factors)) {
      e.apparent_contributing_factors = "Please select at least one option.";
    }

    // Required booleans (must answer yes/no)
    ["any_injuries", "non_vehicle_property_damage", "witnesses_present"]
      .forEach((id) => { if (form[id] !== true && form[id] !== false) e[id] = "Please choose Yes or No."; });

    // Group: road_type_and_controls (all fields required)
    const rtc = form.road_type_and_controls;
    if (!rtc || typeof rtc !== "object") {
      e["road_type_and_controls.location_type"] = "This field is required.";
      e["road_type_and_controls.posted_speed_limit"] = "This field is required.";
      e["road_type_and_controls.traffic_control"] = "This field is required.";
    } else {
      if (isBlank(rtc.location_type)) {
        e["road_type_and_controls.location_type"] = "Please select an option.";
      }
      if (isBlank(rtc.posted_speed_limit)) {
        e["road_type_and_controls.posted_speed_limit"] = "This field is required.";
      } else if (typeof rtc.posted_speed_limit !== "number") {
        e["road_type_and_controls.posted_speed_limit"] = "Speed must be a number.";
      }
      if (isBlank(rtc.traffic_control)) {
        e["road_type_and_controls.traffic_control"] = "Please select an option.";
      }
    }

    // Vehicles (required - must match number_of_vehicles_involved)
    const vehicleCount = parseVehicleCount(form.number_of_vehicles_involved);
    const vehicles = Array.isArray(form.vehicles) ? form.vehicles : [];
    
    // Only validate the number of vehicles that should be displayed
    const activeVehicles = vehicleCount !== null && vehicleCount > 0 
      ? vehicles.slice(0, vehicleCount) 
      : vehicles;
    
    if (!activeVehicles.length) {
      e.vehicles = "Please add vehicle details.";
    } else if (vehicleCount !== null && vehicleCount > 0 && activeVehicles.length < vehicleCount) {
      e.vehicles = `Please add details for ${vehicleCount} vehicle${vehicleCount > 1 ? "s" : ""}.`;
    } else {
      // Validate only the active vehicles (not the old ones that are hidden)
      activeVehicles.forEach((vehicle, idx) => {
        ["role", "vehicle_type_make_model", "licence_plate", "damage_description"].forEach((field) => {
          if (isBlank(vehicle[field])) {
            e[`vehicles[${idx}].${field}`] = "This field is required.";
          }
        });
        if (isBlank(vehicle.pre_crash_manoeuvre)) {
          e[`vehicles[${idx}].pre_crash_manoeuvre`] = "Please select an option.";
        }
        // Handle both kmh and mph field names
        const speedField = vehicle.approx_speed_mph !== undefined ? 'approx_speed_mph' : 'approx_speed_kmh';
        if (isBlank(vehicle[speedField])) {
          e[`vehicles[${idx}].${speedField}`] = "This field is required.";
        } else if (typeof vehicle[speedField] !== "number") {
          e[`vehicles[${idx}].${speedField}`] = "Speed must be a number.";
        }
      });
    }

    // Gated follow-ups (only required if parent is true)
    if (form.any_injuries === true) {
      const injuries = Array.isArray(form.injury_table) ? form.injury_table : [];
      if (injuries.length === 0) {
        e.injury_table = "Please add at least one injured person.";
      } else {
        // Validate each injury row (all fields required)
        injuries.forEach((injury, idx) => {
          ["role", "vehicle_number", "injury_description"].forEach((field) => {
            if (isBlank(injury[field])) {
              e[`injury_table[${idx}].${field}`] = "This field is required.";
            }
          });
          if (isBlank(injury.severity)) {
            e[`injury_table[${idx}].severity`] = "Please select an option.";
          }
        });
      }
    }
    
    if (form.non_vehicle_property_damage === true && isBlank(form.property_damage_description)) {
      e.property_damage_description = "This field is required.";
    }
    
    if (form.witnesses_present === true && isBlank(form.witnesses_names_contacts)) {
      e.witnesses_names_contacts = "This field is required.";
    }
    
    // additional_comments is the ONLY optional field - no validation needed
    
    return e;
  };

  const scrollToFirstError = (errs) => {
    const firstId = Object.keys(errs)[0];
    if (!firstId) return;
    const el = document.getElementById(`field-${firstId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (!schema) {
    return (
      <div className="App">
        <div className="page-shell">
          <div className="two-col">
            <ScenarioPane text={scenarioText} />
            <div className="main-col">
              <div className="chat-header">
                <h2>🧙‍♂️ FormGenius</h2>
                <p>Loading form…</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = buildErrors(data);
    if (Object.keys(errs).length) {
      setErrors(errs);
      setTriedSubmit(true);
      scrollToFirstError(errs);
      return; // block submit
    }
    const payload = {
      form_data: data,
      meta: {
        room_id: roomId,
        submitted_at: new Date().toISOString(),
        source: "plain_form"
      }
    };
    const baseUrl = process.env.NODE_ENV === 'development' 
      ? 'http://localhost:5000' 
      : window.location.origin;
    await fetch(`${baseUrl}/api/plain-form/${roomId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    
    // Notify parent frame (e.g., Qualtrics) that form is submitted
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'formSubmitted',
        status: 'success',
        room: roomId,
        timestamp: new Date().toISOString()
      }, 'https://migroup.qualtrics.com');
      console.log("✅ Notified parent frame of successful submission");
    }
  };

  if (submitted) {
    return (
      <div className="App">
        <div className="page-shell">
          <div className="two-col">
            <ScenarioPane text={scenarioText} />
            <div className="main-col">
              <div className="chat-header">
                <h2>🧙‍♂️ FormGenius</h2>
              </div>
              <div className="chat-window">
                <div className="messages" style={{ padding: 20 }}>
                  <div style={{
                    padding: '20px 25px',
                    margin: '20px 0',
                    backgroundColor: '#d4edda',
                    border: '2px solid #28a745',
                    borderRadius: '12px',
                    color: '#155724',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    textAlign: 'center',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ fontSize: '2rem', marginBottom: '10px' }}>✅</div>
                    <div style={{ marginBottom: '15px' }}>Form submitted successfully!</div>
                    <div style={{
                      padding: '12px 20px',
                      backgroundColor: '#fff',
                      border: '2px dashed #28a745',
                      borderRadius: '8px',
                      fontSize: '1.05rem',
                      fontWeight: '700',
                      color: '#28a745'
                    }}>
                      👉 Click the arrow in the bottom right to continue with the survey
                    </div>
                  </div>
                  <CompletionSummary data={data} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Simple error summary banner shown after a submit attempt
  const errorPaths = Object.keys(errors);
  const showAllErrors = triedSubmit;

  return (
    <div className="App">
      <div className="page-shell">
        <div className="two-col">
          <ScenarioPane text={scenarioText} />
          <div className="main-col">
            <div className="chat-header">
              <h2>🧙‍♂️ FormGenius</h2>
              <p className="chat-header-instruction">
                <strong>💡 How to use this form:</strong><br/>
                Fill out all required fields based on the information from the scenario. 
                You can listen to the audio or refer to the scenario panel on the left for details.
              </p>
            </div>

            {triedSubmit && errorPaths.length > 0 && (
              <div className="error-summary">
                <strong>{errorPaths.length} field{errorPaths.length > 1 ? "s" : ""} need your attention.</strong>{" "}
                {errorPaths.slice(0, 5).map((p, i) => (
                  <span key={p}>
                    {i > 0 ? " · " : " "}
                    <a
                      href="#!"
                      onClick={(e) => {
                        e.preventDefault();
                        const el = document.querySelector(`[data-path="${CSS.escape(p)}"]`);
                        el?.scrollIntoView({ behavior: "smooth", block: "center" });
                      }}
                    >
                      {p}
                    </a>
                  </span>
                ))}
                {errorPaths.length > 5 ? " · …" : ""}
              </div>
            )}

            <form className="chat-window" onSubmit={handleSubmit}>
              <div className="messages" style={{ padding: 20 }}>
                {schema.questions.map((q) => {
            // Handle gate structures (new schema format)
            if (q.isGate && q.gateOn && q.whenValues) {
              const shouldShow = q.whenValues.includes(data[q.gateOn]);
              if (!shouldShow) return null;
              
              // Render the gated fields
              return (
                <div key={q.id}>
                  {q.fields && q.fields.map((field) => (
                    <div key={field.id} id={`field-${field.id}`} style={{ marginBottom: 16 }}>
                      {field.type === 'table' ? (
                        <InjuryTable
                          tq={field}
                          value={data[field.id]}
                          onChange={(v)=>setField(field.id, v)}
                          errors={errors}
                          showAll={showAllErrors}
                        />
                      ) : (
                        <>
                          <div className="ui-instructions" style={{ fontWeight: 600, marginBottom: 8 }}>
                            {field.question} <span style={{ color: '#e53e3e' }}>*</span>
                          </div>
                          <Field
                            q={field}
                            value={data[field.id]}
                            onChange={(v) => setField(field.id, v)}
                            path={field.id}
                            error={errors[field.id]}
                            showError={showAllErrors || !!errors[field.id]}
                          />
                        </>
                      )}
                    </div>
                  ))}
                </div>
              );
            }
            
            // OLD conditional follow-ups (for backward compatibility)
            if (q.id === "any_injuries" && data.any_injuries === true && q.followup_if_yes) {
              return (
                <div key={q.id}>
                  <div className="ui-instructions" style={{ fontWeight: 600 }}>
                    {q.question} <span style={{ color: '#e53e3e' }}>*</span>
                  </div>
                  <Field
                    q={q}
                    value={data[q.id]}
                    onChange={(v) => setField(q.id, v)}
                    path={q.id}
                    error={errors[q.id]}
                    showError={showAllErrors || !!errors[q.id]}
                  />
                  <div id="field-injury_table">
                    <InjuryTable
                      tq={q.followup_if_yes}
                      value={data[q.followup_if_yes.id]}
                      onChange={(v)=>setField(q.followup_if_yes.id, v)}
                      errors={errors}
                      showAll={showAllErrors}
                    />
                    {triedSubmit && errors["injury_table"] && (
                      <div className="error-message">{errors["injury_table"]}</div>
                    )}
                  </div>
                </div>
              );
            }

            if (q.id === "any_injuries") {
              return (
                <div key={q.id}>
                  <div className="ui-instructions" style={{ fontWeight: 600 }}>
                    {q.question} <span style={{ color: '#e53e3e' }}>*</span>
                  </div>
                  <Field
                    q={q}
                    value={data[q.id]}
                    onChange={(v) => setField(q.id, v)}
                    path={q.id}
                    error={errors[q.id]}
                    showError={showAllErrors || !!errors[q.id]}
                  />
                </div>
              );
            }

            if (q.id === "non_vehicle_property_damage" && q.followup_if_yes) {
              return (
                <div key={q.id} id={`field-${q.id}`}>
                  <div className="ui-instructions" style={{ fontWeight: 600 }}>
                    {q.question} <span style={{ color: '#e53e3e' }}>*</span>
                  </div>
                  <Field
                    q={q}
                    value={data[q.id]}
                    onChange={(v)=>setField(q.id, v)}
                    invalid={triedSubmit && !!errors[q.id]}
                  />
                  {triedSubmit && errors[q.id] && <div className="error-message">{errors[q.id]}</div>}

                  {data.non_vehicle_property_damage === true && (
                    <div id="field-property_damage_description" style={{ marginTop: 8 }}>
                      <Field
                        q={q.followup_if_yes}
                        value={data[q.followup_if_yes.id]}
                        onChange={(v)=>setField(q.followup_if_yes.id, v)}
                        invalid={triedSubmit && !!errors["property_damage_description"]}
                      />
                      {triedSubmit && errors["property_damage_description"] && (
                        <div className="error-message">{errors["property_damage_description"]}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            if (q.id === "witnesses_present" && q.followup_if_yes) {
              return (
                <div key={q.id} id={`field-${q.id}`}>
                  <div className="ui-instructions" style={{ fontWeight: 600 }}>
                    {q.question} <span style={{ color: '#e53e3e' }}>*</span>
                  </div>
                  <Field
                    q={q}
                    value={data[q.id]}
                    onChange={(v)=>setField(q.id, v)}
                    invalid={triedSubmit && !!errors[q.id]}
                  />
                  {triedSubmit && errors[q.id] && <div className="error-message">{errors[q.id]}</div>}

                  {data.witnesses_present === true && (
                    <div id="field-witnesses_names_contacts" style={{ marginTop: 8 }}>
                      <Field
                        q={q.followup_if_yes}
                        value={data[q.followup_if_yes.id]}
                        onChange={(v)=>setField(q.followup_if_yes.id, v)}
                        invalid={triedSubmit && !!errors["witnesses_names_contacts"]}
                      />
                      {triedSubmit && errors["witnesses_names_contacts"] && (
                        <div className="error-message">{errors["witnesses_names_contacts"]}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            // regular
            return (
              <div key={q.id} id={`field-${q.id}`} style={{ marginBottom: 16 }}>
                <div className="ui-instructions" style={{ fontWeight: 600 }}>
                  {q.question} {q.id !== "additional_comments" && <span style={{ color: '#e53e3e' }}>*</span>}
                </div>

                {q.type === "group" && (
                  <Group
                    gq={q}
                    value={data[q.id]}
                    onChange={(v)=>setField(q.id, v)}
                    errors={errors}
                    triedSubmit={triedSubmit}
                  />
                )}

                {q.type === "repeat_group" && (
                  <VehiclesRepeat
                    rq={q}
                    value={data[q.id]}
                    onChange={(v)=>setField(q.id, v)}
                    errors={errors}
                    showAll={showAllErrors}
                    vehicleCount={parseVehicleCount(data["number_of_vehicles_involved"])}
                  />
                )}

                {q.type !== "group" && q.type !== "repeat_group" && (
                  <>
                    <Field
                      q={q}
                      value={data[q.id]}
                      onChange={(v)=>setField(q.id, v)}
                      invalid={triedSubmit && !!errors[q.id]}
                    />
                    {triedSubmit && errors[q.id] && (
                      <div className="error-message">{errors[q.id]}</div>
                    )}
                  </>
                )}
              </div>
            );
          })}
          </div>

          <div className="summary-submit-container">
            <button className="summary-submit-button" type="submit">Submit Form</button>
          </div>
        </form>
          </div>
        </div>
      </div>
    </div>
  );
}
